/**
 * DropdownItem - Standard item for dropdown menus with optional checkmark.
 *
 * Replaces repeated inline-styled <button> elements across toolbar dropdowns.
 * Hover handled via CSS class .dropdown-item (no inline backgroundColor).
 */

import { memo, type ReactNode, type AriaRole } from "react";

import { Check } from "@phosphor-icons/react";

/**
 * ARIA roles that support the `aria-selected` attribute per the WAI-ARIA spec.
 * Only these roles should receive `aria-selected`; roles like `menuitem` do not
 * support it and would cause accessibility violations.
 *
 * Covers the roles realistically used by DropdownItem callers in this project.
 * Extend this set if new callers use additional WAI-ARIA roles that support
 * `aria-selected` (e.g. "row", "gridcell") — see the full spec list at
 * https://www.w3.org/TR/wai-aria-1.2/#aria-selected
 */
const ARIA_SELECTED_ROLES = new Set<AriaRole>(["option", "tab", "treeitem"]);

/**
 * ARIA roles that support the `aria-checked` attribute per the WAI-ARIA spec.
 * `menuitemcheckbox` and `menuitemradio` are the primary roles that require it.
 */
const ARIA_CHECKED_ROLES = new Set<AriaRole>([
  "menuitemcheckbox",
  "menuitemradio",
]);

interface DropdownItemProps {
  /** Whether this item is currently selected */
  isSelected?: boolean;
  /**
   * Whether this item has keyboard focus (via Arrow key navigation).
   * When true, a visual highlight is applied matching the hover style
   * so keyboard users get the same affordance as mouse users.
   */
  isFocused?: boolean;
  /** When true the item is rendered but cannot be activated */
  disabled?: boolean;
  /** Click handler */
  onClick: () => void;
  /** Primary label */
  children: ReactNode;
  /** Optional description text below the label */
  description?: string;
  /** Show checkmark column (default true) */
  showCheckmark?: boolean;
  /** Trailing content (e.g. color swatches) */
  trailing?: ReactNode;
  /**
   * ARIA role — when set to a role that supports `aria-selected` (e.g. "option",
   * "tab", "treeitem"), `aria-selected` is also emitted. Roles like "menuitem"
   * do NOT support `aria-selected` and will not emit it.
   * When set to "menuitemcheckbox" or "menuitemradio", `aria-checked` is emitted
   * instead, derived from `isSelected` unless overridden via `aria-checked`.
   */
  role?: AriaRole;
  /** aria-selected override (only emitted when role supports aria-selected) */
  "aria-selected"?: boolean;
  /** aria-checked override (only emitted when role supports aria-checked, e.g. menuitemcheckbox) */
  "aria-checked"?: boolean;
  /** HTML id — used for aria-activedescendant on the parent listbox */
  id?: string;
}

export const DropdownItem = memo(function DropdownItem({
  isSelected = false,
  isFocused = false,
  disabled = false,
  onClick,
  children,
  description,
  showCheckmark = true,
  trailing,
  role,
  "aria-selected": ariaSelected,
  "aria-checked": ariaChecked,
  id,
}: DropdownItemProps): JSX.Element {
  const hasDescription = !!description;

  // Determine whether a semantic ARIA state attribute conveys the selection.
  // When neither aria-selected nor aria-checked is emitted (e.g. role is
  // "menuitem" or absent), we fall back to a visually-hidden text annotation
  // so screen readers still announce the active selection.
  const emitsAriaSelected = !!(role && ARIA_SELECTED_ROLES.has(role));
  const emitsAriaChecked = !!(role && ARIA_CHECKED_ROLES.has(role));
  const needsFallbackSelectedText =
    isSelected && !emitsAriaSelected && !emitsAriaChecked;

  return (
    <button
      id={id}
      type="button"
      role={role}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-selected={
        emitsAriaSelected ? (ariaSelected ?? isSelected) : undefined
      }
      aria-checked={emitsAriaChecked ? (ariaChecked ?? isSelected) : undefined}
      onClick={onClick}
      className={[
        "dropdown-item",
        "flex items-center w-full text-left text-sm whitespace-nowrap",
        disabled
          ? "cursor-not-allowed text-slate-400"
          : "cursor-pointer text-slate-900",
        hasDescription
          ? "min-h-[36px] py-1.5 pr-3 pl-2"
          : "h-8 py-0 pr-[15px] pl-[9px]", // pl-[9px]/pr-[15px] pixel-align with DropdownPanel's px-[9px] left-padding so the checkmark column visually aligns with the panel edge — if DropdownPanel padding changes, update this value to match
        isSelected ? "dropdown-item-selected" : "",
        isFocused ? "dropdown-item-focused" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Checkmark space */}
      {showCheckmark && (
        <span
          className={[
            "inline-flex items-center justify-center w-5 h-5 flex-shrink-0",
            hasDescription ? "mr-2" : "mr-2.5",
          ].join(" ")}
        >
          {isSelected && (
            <Check
              size={16}
              weight="bold"
              className="text-brand-600"
              aria-hidden="true"
            />
          )}
        </span>
      )}
      {/* Visually-hidden fallback for screen readers when no ARIA state attribute
          (aria-selected / aria-checked) is emitted for this role. Ensures that
          the active selection is still announced even for plain menuitem roles. */}
      {needsFallbackSelectedText && <span className="sr-only">(selected)</span>}

      {/* Content */}
      {hasDescription ? (
        <div className="flex-1">
          <div>{children}</div>
          <div className="text-xs text-slate-500 mt-px">{description}</div>
        </div>
      ) : (
        <span className="flex-1">{children}</span>
      )}

      {trailing}
    </button>
  );
});
